import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { Button } from "@shared/ui/button";
import { Badge } from "@shared/ui/badge";
import { Switch } from "@shared/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@shared/ui/tooltip";
import { toast } from "@shared/ui/use-toast";
import {
  Droplet,
  Zap,
  Flame,
  Wifi,
  Car,
  CircuitBoard,
  Thermometer,
  Pipette,
  Trash2,
  Droplets,
  Building,
  ToggleLeft,
  Info,
  Save,
  ArrowUpDown,
  Check,
  X,
  InfoIcon,
} from "lucide-react";
import {
  useAllUtilityServices,
  useUpdateUnit,
  useUtilityStatus,
} from "../../hooks";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { logEvent } from "@shared/analytics/analytics";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@shared/ui/accordion";

const getServiceIcon = (name: string) => {
  switch (name) {
    case "Water":
      return <Droplet className="h-5 w-5" />;
    case "Electric":
      return <Zap className="h-5 w-5" />;
    case "Gas":
      return <Flame className="h-5 w-5" />;
    case "Broadband":
      return <Wifi className="h-5 w-5" />;
    case "EV":
      return <Car className="h-5 w-5" />;
    case "Micro-Grid":
      return <CircuitBoard className="h-5 w-5" />;
    case "Hot Water":
      return <Thermometer className="h-5 w-5" />;
    case "Sewer":
      return <Pipette className="h-5 w-5" />;
    case "Waste":
      return <Trash2 className="h-5 w-5" />;
    case "Waste Water":
      return <Droplets className="h-5 w-5" />;
    case "Property Tax":
      return <Building className="h-5 w-5" />;
    default:
      return <ToggleLeft className="h-5 w-5" />;
  }
};

export const UtilityServiceTab: React.FC = () => {
  const { data } = useAllUtilityServices();
  const { mutate: updateUnit } = useUpdateUnit();
  const { mutate: updateUtilityStatus } = useUtilityStatus();

  const [services, setServices] = useState<any[]>([]);
  const utilityID = useRemoteUtilityId();
  // Initialize services after data is loaded
  useEffect(() => {
    if (data && Array.isArray(data)) {
      const initialized = data.map((s, i) => ({
        ...s,
        id: String(s.id),
        icon: getServiceIcon(s.name),
        order: i + 1,
        active: s.isActive,
        metered: s.isMeterRequired,
        unit: s.utilityUnit || s.units?.[0] || "",
      }));
      setServices(initialized);
    }
  }, [data]);

  const [isEditing, setIsEditing] = useState(false);
  const [draggedService, setDraggedService] = useState<any | null>(null);

  const handleFieldChange = (
    id: string,
    field: string,
    value: any,
    item?: any
  ) => {
    console.log("item", item);
    console.log(`🔄 Changing ${field} for service ${id} to`, value);

    // Update local state
    setServices((prev) =>
      prev.map((service) =>
        service.id === id ? { ...service, [field]: value } : service
      )
    );

    // Conditional API call logic
    const service = services.find((s) => s.id === id);
    if (!service) return;

    const serviceId = service.serviceId;
    console.log("field", field);
    console.log("value", value);

    switch (field) {
      case "unit":
        const payload = {
          id: id,
          service_id: serviceId,
          utility_unit: value,
        };
        updateUnit(payload, {
          onSuccess: () => {
            logEvent("Utility Service Unit Update", {
              ...payload,
            });
          },
        });
        break;
      case "metered":
        const meterPayload = {
          is_meter_required: value,
          id: id,
        };
        updateUnit(meterPayload, {
          onSuccess: () => {
            logEvent("Utility Service Metering Update", {
              ...meterPayload,
            });
          },
        });
        break;
      case "active":
        const statusPayload = {
          is_active: value,
          id: item.serviceId,
          utility: utilityID,
        };
        updateUtilityStatus(statusPayload, {
          onSuccess: () => {
            logEvent("Utility Service Status Update", {
              ...statusPayload,
            });
          },
        });
        break;

      default:
        break;
    }
  };

  const saveChanges = () => {
    console.log("Saving services configuration:", services);
toast({
  title: "Success",
  description: "Utility services configuration saved successfully",
});

    setIsEditing(false);
  };

  return (
    <Card className="flex flex-col gap-4">
      <CardHeader className="flex flex-row items-start justify-between">
        <div>
          <CardTitle className="page-subheading">Utility Services</CardTitle>
          <CardDescription className="page-subtitle">
            Configure the utility services available to customers
          </CardDescription>
        </div>
        <div className="flex items-center space-x-2">
          {isEditing && (
            <Button onClick={saveChanges}>
              <Save className="h-4 w-4 mr-2" /> Save Changes
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Accordion type="single" collapsible className="mb-4">
          <AccordionItem value="help">
            <AccordionTrigger className="text-sm font-medium">
              <div className="flex items-center">
                <InfoIcon className="h-4 w-4 mr-2" />
                Need help with utility services?
              </div>
            </AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground">
              <p className="mb-2">
             Configure the utility services available in SMART360:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>
                  Enable or disable each utility service.
                </li>
                <li>
                 Mark services as metered or unmetered.
                </li>
                <li>
                  Set the unit of measurement (e.g., m³, kWh, gallons).
                </li>
                <li>
                 Ensure only relevant services are active for billing and operations.
                </li>
              </ul>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        {isEditing && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
            <div className="flex items-start gap-2">
              <Info className="h-5 w-5 mt-0.5" />
              <div>
                <p className="font-medium">Edit Mode Active</p>
                <p>
                  Drag and drop services to reorder. Toggle switches to
                  configure services.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="rounded-md border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-center w-[5%]">Sr No</TableHead>
                <TableHead className="w-[25%]">Service</TableHead>
                <TableHead className="text-center w-[15%]">Status</TableHead>
                <TableHead className="text-center w-[20%]">Metering</TableHead>
                <TableHead className="w-[35%]">Unit of Measurement</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {services
                .sort((a, b) => a.order - b.order)
                .map((service) => (
                  <TableRow
                    key={service.id}
                    draggable={isEditing}
                    onDragStart={() => setDraggedService(service)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      if (!draggedService || draggedService.id === service.id)
                        return;
                      const newList = [...services];
                      const fromIdx = newList.findIndex(
                        (s) => s.id === draggedService.id
                      );
                      const toIdx = newList.findIndex(
                        (s) => s.id === service.id
                      );
                      const [moved] = newList.splice(fromIdx, 1);
                      newList.splice(toIdx, 0, moved);
                      setServices(
                        newList.map((s, i) => ({ ...s, order: i + 1 }))
                      );
                      setDraggedService(null);
                    }}
                    onDragEnd={() => setDraggedService(null)}
                    className={isEditing ? "cursor-move hover:bg-gray-50" : ""}
                  >
                    <TableCell className="text-center font-medium">
                      {isEditing ? (
                        <ArrowUpDown className="h-4 w-4 text-gray-400 mx-auto" />
                      ) : (
                        service.order
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div
                          className={`p-1.5 rounded-md ${
                            service.active
                              ? "bg-primary/10 text-primary"
                              : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          {service.icon}
                        </div>
                        <span
                          className={`font-medium ${
                            !service.active && "text-gray-500"
                          }`}
                        >
                          {service.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={service.active}
                        // disabled={!service.metered} // Disable when is_meter_required is false
                        onCheckedChange={() =>
                          handleFieldChange(
                            service.id,
                            "active",
                            !service.active,
                            service
                          )
                        }
                      />
                    </TableCell>
                    <TableCell className="text-center">
                      {service.active ? (
                        <div className="flex flex-col items-center gap-1">
                          <Switch
                            checked={service.metered}
                            onCheckedChange={() =>
                              handleFieldChange(
                                service.id,
                                "metered",
                                !service.metered
                              )
                            }
                          />
                          <Badge
                            variant="outline"
                            className={
                              service.metered
                                ? "bg-green-50 text-green-700 border-green-200 text-xs"
                                : "bg-gray-50 text-gray-700 border-gray-200 text-xs"
                            }
                          >
                            {service.metered ? (
                              <>
                                <Check className="h-3 w-3" /> Metered
                              </>
                            ) : (
                              <>
                                <X className="h-3 w-3" /> Unmetered
                              </>
                            )}
                          </Badge>
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {service.active ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div>
                                <Select
                                  value={service.unit}
                                  onValueChange={(value) =>
                                    handleFieldChange(service.id, "unit", value)
                                  }
                                >
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select unit" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {service.units?.map((unit: string) => (
                                      <SelectItem
                                        key={`${service.id}-${unit}`}
                                        value={unit}
                                      >
                                        {unit}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>
                                Select unit of measurement for {service.name}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default UtilityServiceTab;
